import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { AlertTriangle, Check, Loader2, LogIn, LogOut, Wallet } from "lucide-react";
import { api, formatApiError } from "../../api";
import { DESK } from "../../constants/testIds";
import { useDeskData } from "../../contexts/DeskDataContext";
import { formatCurrency } from "../../lib/format";
import { fadeUp } from "../../lib/motion";
import { Button } from "../../components/ui/button";
import { toast } from "../../components/ui/sonner";
import { FormField } from "../../components/common/FormField";
import { SubmitButton } from "../../components/common/SubmitButton";
import { useSuccessPulse } from "../../hooks/useSuccessPulse";

// UX-FD-01 §1 SCR-P02-01: clock-in + open cash float. No FD-permitted endpoint exists to query
// whether THIS staffer's cash drawer is already open for the day (only `GET /shifts/current`
// exists, and that answers clock-in/out attendance, not the cash-float doc) — so "drawer open"
// state is tracked client-side for this browser session (localStorage), a documented, honest
// adaptation rather than fabricating a lookup endpoint. Clock-in state IS server-verifiable
// (`on_shift_staff_refs`) and is used for real.
export default function DeskShiftStart() {
  const { me, branchId } = useDeskData();
  const reducedMotion = useReducedMotion();
  const [floatAmount, setFloatAmount] = useState("");
  const drawerKey = `fitfrik-desk-drawer-${me?.id}-${branchId}`;
  const shiftIdKey = `fitfrik-desk-shiftid-${me?.id}-${branchId}`;
  const [drawerOpen, setDrawerOpen] = useState(() => !!localStorage.getItem(drawerKey));
  const [countedAmount, setCountedAmount] = useState("");
  const [handoverTo, setHandoverTo] = useState("");
  const [handoverResult, setHandoverResult] = useState(null);
  const queryClient = useQueryClient();
  const [drawerSuccess, drawerPulse] = useSuccessPulse();
  const [handoverSuccess, handoverPulse] = useSuccessPulse();

  const shiftQ = useQuery({
    queryKey: ["desk-shift-current", branchId],
    queryFn: async () => (await api.get("/shifts/current", { params: { branch_id: branchId } })).data,
    enabled: !!branchId,
  });

  const clockedIn = shiftQ.data?.on_shift_staff_refs?.includes(me?.id);

  const clockInMutation = useMutation({
    mutationFn: () => api.post("/clock", { branch_id: branchId, type: "in" }),
    onSuccess: () => {
      toast.success("Clocked in.");
      queryClient.invalidateQueries({ queryKey: ["desk-shift-current"] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const clockOutMutation = useMutation({
    mutationFn: () => api.post("/clock", { branch_id: branchId, type: "out" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["desk-shift-current"] }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const openDrawerMutation = useMutation({
    mutationFn: () => api.post("/shift-cash/open", { branch_id: branchId, float_amount: Math.round(Number(floatAmount) * 100) }),
    onSuccess: (res) => drawerPulse(() => {
      toast.success("Cash drawer opened.");
      localStorage.setItem(drawerKey, "1");
      localStorage.setItem(shiftIdKey, res.data.id);
      setDrawerOpen(true);
    }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  // UX-FD-02 §1 SCR-P02-70: no GET endpoint exists to look up an open shift-cash doc by staffer
  // — the id is the one this SAME browser session stored when it opened the drawer (documented,
  // same client-tracked adaptation as `drawerOpen` above).
  const handoverMutation = useMutation({
    mutationFn: () => api.post(
      `/shift-cash/${localStorage.getItem(shiftIdKey)}/handover`,
      { counted_amount: Math.round(Number(countedAmount) * 100), handover_to: handoverTo || null },
    ),
    onSuccess: (res) => handoverPulse(() => {
      setHandoverResult(res.data);
      localStorage.removeItem(drawerKey);
      localStorage.removeItem(shiftIdKey);
      clockOutMutation.mutate();
    }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <div data-testid={DESK.shiftPage} className="space-y-6 max-w-lg">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Start your shift</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Clock in and open your cash drawer before taking any payments.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.04)} className="rounded-[14px] p-5 flex items-center justify-between" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <div className="flex items-center gap-3">
          <LogIn size={18} style={{ color: clockedIn ? "var(--scarlet-pos)" : "#68686E" }} />
          <div>
            <p className="font-semibold text-sm" style={{ color: "#141416" }}>Attendance</p>
            <p data-testid={DESK.clockStatus} className="text-xs" style={{ color: "#68686E" }}>{clockedIn ? "Clocked in" : "Not clocked in yet"}</p>
          </div>
        </div>
        <Button
          data-testid={DESK.clockInButton}
          disabled={clockedIn || clockInMutation.isPending}
          onClick={() => clockInMutation.mutate()}
          className="text-white gap-2"
          style={{ background: "#141416" }}
        >
          {clockInMutation.isPending ? <Loader2 size={15} className="animate-spin" /> : clockedIn ? <Check size={15} /> : null}
          {clockedIn ? "Clocked in" : "Clock in"}
        </Button>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.08)} className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <div className="flex items-center gap-3">
          <Wallet size={18} style={{ color: drawerOpen ? "var(--scarlet-pos)" : "#68686E" }} />
          <div>
            <p className="font-semibold text-sm" style={{ color: "#141416" }}>Cash drawer</p>
            <p data-testid={DESK.drawerStatus} className="text-xs" style={{ color: "#68686E" }}>
              {drawerOpen ? "Drawer opened this session" : "Enter your opening float and open the drawer"}
            </p>
          </div>
        </div>
        {!drawerOpen && (
          <div className="flex items-end gap-3">
            <FormField required type="number" label="Opening float (₹)" testId={DESK.floatAmountInput} value={floatAmount} onChange={setFloatAmount} className="flex-1" />
            <SubmitButton
              testId={DESK.openDrawerButton} disabled={!floatAmount}
              pending={openDrawerMutation.isPending} success={drawerSuccess}
              onClick={() => openDrawerMutation.mutate()}
              label="Open drawer" successLabel="Opened"
            />
          </div>
        )}
        {drawerOpen && floatAmount && (
          <p className="text-xs" style={{ color: "#68686E" }}>Opened with {formatCurrency(Math.round(Number(floatAmount) * 100))} float.</p>
        )}
      </motion.div>

      {drawerOpen && !handoverResult && (
        <motion.div {...fadeUp(reducedMotion, 0.12)} className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <p className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: "#141416" }}><LogOut size={15} /> End shift / handover</p>
          <FormField required type="number" label="Cash counted in drawer (₹)" testId={DESK.handoverCountedInput} value={countedAmount} onChange={setCountedAmount} />
          <FormField label="Handing over to (optional)" testId={DESK.handoverToInput} value={handoverTo} onChange={setHandoverTo} />
          <SubmitButton
            testId={DESK.handoverSubmit} disabled={!countedAmount}
            pending={handoverMutation.isPending} success={handoverSuccess}
            onClick={() => handoverMutation.mutate()}
            label="Hand over & clock out" successLabel="Handed over"
          />
        </motion.div>
      )}

      {handoverResult && (
        <motion.div {...fadeUp(reducedMotion)} data-testid={DESK.handoverResult} className="rounded-[14px] p-5 space-y-2 celebrate-pop" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <p className="flex items-center gap-2 font-semibold text-sm" style={{ color: "var(--scarlet-pos)" }}><Check size={16} /> Shift handed over</p>
          <div className="text-sm grid grid-cols-2 gap-1" style={{ color: "#141416" }}>
            <span style={{ color: "#68686E" }}>Expected</span><span>{formatCurrency(handoverResult.expected_amount)}</span>
            <span style={{ color: "#68686E" }}>Counted</span><span>{formatCurrency(handoverResult.counted_amount)}</span>
            <span style={{ color: "#68686E" }}>Variance</span><span>{formatCurrency(handoverResult.variance)}</span>
          </div>
          {handoverResult.cash_mismatch && (
            <p data-testid={DESK.handoverMismatchFlag} className="flex items-center gap-1.5 text-sm font-medium" style={{ color: "var(--scarlet-neg)" }}>
              <AlertTriangle size={14} /> Cash mismatch flagged for manager review.
            </p>
          )}
        </motion.div>
      )}
    </div>
  );
}
